'use client';

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { solicitudService } from '@/lib/api/solicitudService';
import { Solicitud } from '@/types/solicitud';
import { SolicitudTable } from './SolicitudTable';
import { SolicitudFormModal } from './SolicitudFormModal';
import { SolicitudDetail } from './SolicitudDetail';
import { useSolicitudFilter, SolicitudFilterUI } from './solicitudFilters';
import { Button } from '@/components/ui/Button';
import ErrorMessage from '@/components/common/ErrorMessage';
import { ModuloVista } from '@/utils/fieldVisibility';
import { useAuth } from '@/hooks/useAuth';

interface SolicitudManagementProps {
  title?: string;
  fixedStatus?: string; // ej: "CONTACTO" para Contactados
  extraFilter?: (s: Solicitud) => boolean; // filtro adicional para módulos (ej. Revisión Documental)
  modulo?: ModuloVista; // Define qué campos mostrar en el detalle
  renderActions?: (s: Solicitud, refresh: () => void) => React.ReactNode; // Acciones custom en la tabla
  initialSolicitudes?: Solicitud[]; // Solicitudes iniciales (para módulos que cargan desde endpoints específicos)
  customFetch?: (setSolicitudes: (s: Solicitud[]) => void, ownerId: string) => Promise<void>; // Función personalizada para cargar solicitudes (recibe ownerId del useAuth)
  showNewButton?: boolean; // Mostrar botón "Nueva Solicitud" (por defecto true, false para módulos específicos)
  /** Monto a mostrar en la tabla: 'solicitado' (default) o 'autorizado' */
  montoColumn?: 'solicitado' | 'autorizado';
  /** Clase CSS extra por fila (para resaltar filas según condición) */
  rowClassName?: (solicitud: Solicitud) => string | undefined;
  /** Modo solo lectura - deshabilita edición de campos en el detalle */
  readOnly?: boolean;
}

const SolicitudManagement: React.FC<SolicitudManagementProps> = ({
  title = "Solicitudes",
  fixedStatus,
  extraFilter,
  modulo = 'all',
  renderActions,
  initialSolicitudes,
  customFetch,
  showNewButton = true,
  montoColumn = 'solicitado',
  rowClassName,
  readOnly = false
}) => {
  const { user } = useAuth();
  const [solicitudes, setSolicitudes] = useState<Solicitud[]>(initialSolicitudes || []);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!initialSolicitudes);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState<Solicitud | null>(null);

  const effectiveExtraFilter = useCallback(
    (s: Solicitud) => {
      if (fixedStatus && (s.solicitudEstatus || '').toUpperCase() !== fixedStatus.toUpperCase()) return false;
      if (extraFilter) return extraFilter(s);
      return true;
    },
    [fixedStatus, extraFilter]
  );

  const {
    filterField,
    setFilterField,
    filterValue,
    setFilterValue,
    searchTerm,
    setSearchTerm,
    filtered,
    baseList,
    clearFilters,
  } = useSolicitudFilter(solicitudes, { extraFilter: effectiveExtraFilter });

  const ownerId = user?.sub || '';

  const fetchSolicitudes = useCallback(async () => {
    // Esperar a que el usuario esté autenticado antes de hacer fetch
    if (!ownerId) {
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      if (customFetch) {
        await customFetch(setSolicitudes, ownerId);
      } else if (!fixedStatus && !initialSolicitudes) {
        // Para el módulo principal de Solicitudes, usar getInProcess
        const response = await solicitudService.getInProcess(ownerId);
        setSolicitudes(response.data || []);
      } else {
        const response = await solicitudService.getAllSummary();
        setSolicitudes(response.data || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Error al cargar las solicitudes');
      console.error('Error fetching solicitudes:', err);
    } finally {
      setIsLoading(false);
    }
  }, [customFetch, fixedStatus, initialSolicitudes, ownerId]);

  useEffect(() => {
    if (initialSolicitudes) {
      setSolicitudes(initialSolicitudes);
      setIsLoading(false);
    } else {
      fetchSolicitudes();
    }
  }, [initialSolicitudes, fetchSolicitudes]);

  const handleCreate = () => {
    setIsModalOpen(true);
  };

  const handleEdit = (solicitud: Solicitud) => {
    if (!solicitud.id) return;
    setError(null);
    setSelectedSolicitud(solicitud);
    setIsDetailOpen(true);
    // SolicitudDetail hace un solo getById al abrir; evita duplicar la petición aquí
  };

  const handleUpdateDetail = (updated: Solicitud) => {
    const statusChanged = fixedStatus && (updated.solicitudEstatus || '').toUpperCase() !== fixedStatus.toUpperCase();
    if (statusChanged) {
      // La solicitud cambió de estatus y ya no pertenece a este módulo → cerrar modal y refrescar
      setIsDetailOpen(false);
      setSelectedSolicitud(null);
      setSolicitudes(prev => prev.filter(s => s.id !== updated.id));
      if (customFetch) fetchSolicitudes();
    } else {
      setSelectedSolicitud(updated);
      setSolicitudes(prev => prev.map(s => s.id === updated.id ? updated : s));
    }
  };

  const handleSubmit = async (data: Partial<Solicitud>) => {
    try {
      if (data.id) {
        await solicitudService.updateFull(data.id, data);
      } else {
        await solicitudService.create(data);
      }
      setIsModalOpen(false);
      await fetchSolicitudes();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar la solicitud');
      throw err;
    }
  };

  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined || value === null) return '$0.00';
    return new Intl.NumberFormat('es-MX', { 
      style: 'currency', 
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {fixedStatus 
              ? `Vista filtrada por estatus: ${fixedStatus}` 
              : "Módulo principal para gestionar solicitudes de crédito a lo largo del proceso"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={fetchSolicitudes} disabled={isLoading}>
            {isLoading ? "Cargando…" : "Recargar"}
          </Button>
          {showNewButton && (
            <Button onClick={handleCreate} icon={<span className="text-lg font-bold">+</span>}>
              Nueva Solicitud
            </Button>
          )}
        </div>
      </div>

      {error && <ErrorMessage message={error} />}

      {/* Filtros Avanzados */}
      <SolicitudFilterUI
        filterField={filterField}
        setFilterField={setFilterField}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filteredCount={filtered.length}
        baseCount={baseList.length}
        countLabel="solicitudes"
      />

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <ErrorMessage message={error} onRetry={fetchSolicitudes} />
      ) : (
        <>
          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
              <div className="text-sm font-semibold text-gray-900">Sin resultados</div>
              <div className="mt-1 text-sm text-gray-500">
                Ajusta filtros/búsqueda o crea una nueva solicitud.
              </div>
              <div className="mt-4 flex justify-center gap-2">
                <Button variant="secondary" onClick={clearFilters}>
                  Limpiar búsqueda
                </Button>
                {showNewButton && (
                  <Button onClick={handleCreate}>Nueva Solicitud</Button>
                )}
              </div>
            </div>
          ) : (
            <>
              <SolicitudTable
                solicitudes={filtered}
                onEdit={handleEdit}
                renderActions={renderActions ? (s) => renderActions(s, fetchSolicitudes) : undefined}
                montoColumn={montoColumn}
                rowClassName={rowClassName}
              />
              <div className="text-right text-xs text-gray-400 px-2">
                Total: {filtered.length} solicitudes
              </div>
            </>
          )}
        </>
      )}

      <SolicitudFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
      />

      <SolicitudDetail
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        solicitud={selectedSolicitud}
        onUpdate={handleUpdateDetail}
        modulo={modulo}
        readOnly={readOnly}
      />
    </div>
  );
};

export default SolicitudManagement;
